import { CHART_COLORS } from '@/features/chart/lib/constants/chart-colors'
import {
  CROSS_EXCHANGE_CVD_INPUTS,
  CROSS_EXCHANGE_FUNDING_INPUTS,
  CROSS_EXCHANGE_OI_INPUTS,
} from '@/features/chart/lib/series-sources/cross-exchange-source'
import {
  type ChartSeriesSourceId,
  REFRESH_NONCE_INPUT_ID,
} from '@/lib/tradingview/datafeeds/series-sources'
import {
  DEFAULT_DEPTH_TIER_BPS,
  ORDERBOOK_DEPTH_TIERS_BPS,
} from '@/service/orderbook-metrics.types'
import {
  ChartAnalyticsMetric,
  ChartAnalyticsOption,
  ChartAnalyticsPlacement,
} from '@/types/dashboard'
import { CandleFamily, type CandleList } from '@/types/generated/public_api'

export type ChartAnalyticsCategory =
  | 'price'
  | 'flow'
  | 'liquidations'
  | 'microstructure'
  | 'volatility'
  | 'block'
  | 'derivatives'
  | 'cross-exchange'

export type ChartAnalyticsSuiteKey =
  | 'price-overlays'
  | 'advanced-volume'
  | 'order-flow-delta'
  | 'liquidation-map'
  | 'market-microstructure'
  | 'volatility-spreads'
  | 'block-included'
  | 'derivatives'
  | 'cross-exchange'

export type StudyInputSpec = {
  /** TV input id, unique within the study (e.g. 'measure') */
  id: string
  /** Label shown in the study-settings dialog */
  name: string
  /** 'select' renders as TV's dropdown ('text' + options) */
  type: 'select' | 'bool' | 'integer' | 'float'
  defval: string | number | boolean
  /** Allowed values, required for 'select' */
  options?: string[]
  /** Display labels per option value (e.g. { base: 'Base' }); 'select' only */
  optionsTitles?: Record<string, string>
  /** Hidden from the study-settings dialog (StudyInputBaseInfo.isHidden). */
  isHidden?: boolean
}

export type ChartAnalyticsDefinition = {
  key: ChartAnalyticsMetric
  label: string
  shortLabel: string
  description: string
  placement: ChartAnalyticsPlacement
  color: string
  category: ChartAnalyticsCategory
  suite: ChartAnalyticsSuiteKey
  discoverable: boolean
  recommendedVisual:
    | 'line'
    | 'dashed-line'
    | 'dots'
    | 'step-line'
    | 'histogram'
    | 'stacked-histogram'
    | 'zero-centered-histogram'
    | 'area'
    | 'candles'
  unit: 'price' | 'percent' | 'base' | 'quote' | 'count' | 'volatility'
  /** CandleList family this metric's columns ride in. CORE is always fetched.
   *  Own-fetch (separate-endpoint) definitions carry `source` instead. */
  family?: CandleFamily
  /** Own-fetch series source that provides this metric (see series-sources.ts).
   *  Mutually exclusive with `family`. */
  source?: ChartSeriesSourceId
  /** User-configurable study inputs, rendered in TV's study-settings dialog */
  inputs?: StudyInputSpec[]
}

export type ChartAnalyticsSuiteDefinition = {
  key: ChartAnalyticsSuiteKey
  label: string
  description: string
  category: ChartAnalyticsCategory
  metrics: ChartAnalyticsMetric[]
  settings: Array<'unit' | 'includeBlockTrades' | 'buySellSplit' | 'showNet' | 'showCounts'>
}

export const CHART_ANALYTICS_DEFINITIONS: ChartAnalyticsDefinition[] = [
  {
    key: 'vwap',
    label: 'VWAP',
    shortLabel: 'VWAP',
    description:
      'Volume-weighted average traded price. Zero-only series should be suppressed for inverse contracts.',
    placement: 'overlay',
    color: CHART_COLORS.overlayVwap,
    category: 'price',
    suite: 'price-overlays',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'price',
    family: CandleFamily.CANDLE_FAMILY_CORE,
    inputs: [
      {
        id: 'lookbackWindow',
        name: 'Lookback Window',
        type: 'integer',
        defval: 1,
      },
    ],
  },
  {
    key: 'twap',
    label: 'TWAP',
    shortLabel: 'TWAP',
    description: 'Time-weighted average price.',
    placement: 'overlay',
    color: CHART_COLORS.overlayTwap,
    category: 'price',
    suite: 'price-overlays',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'price',
    family: CandleFamily.CANDLE_FAMILY_CORE,
  },
  {
    key: 'deltaPercentage',
    label: 'Volume Delta %',
    shortLabel: 'Volume Delta %',
    description: 'Buy versus sell imbalance as a percentage of total volume.',
    placement: 'pane',
    color: '#10B981',
    category: 'flow',
    suite: 'order-flow-delta',
    discoverable: true,
    recommendedVisual: 'zero-centered-histogram',
    unit: 'percent',
    family: CandleFamily.CANDLE_FAMILY_ORDER_FLOW,
  },
  {
    key: 'netBuyVolumeBase',
    label: 'Volume Delta',
    shortLabel: 'Volume Delta',
    description: 'Net aggressive buying volume in configurable base or quote units.',
    placement: 'pane',
    color: '#F59E0B',
    category: 'flow',
    suite: 'order-flow-delta',
    discoverable: true,
    recommendedVisual: 'zero-centered-histogram',
    unit: 'base',
    family: CandleFamily.CANDLE_FAMILY_ORDER_FLOW,
    inputs: [
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'base',
      },
    ],
  },
  {
    key: 'cumulativeVolumeDelta',
    label: 'Cumulative Volume Delta',
    shortLabel: 'Cumulative Volume Delta',
    description: 'Running sum of Volume Delta, showing cumulative aggressive buy/sell pressure.',
    placement: 'pane',
    color: '#22D3EE',
    category: 'flow',
    suite: 'order-flow-delta',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'base',
    family: CandleFamily.CANDLE_FAMILY_ORDER_FLOW,
    inputs: [
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'base',
      },
      {
        id: 'visualStyle',
        name: 'Style',
        type: 'select',
        options: ['line', 'candles'],
        optionsTitles: { line: 'Line', candles: 'Candles' },
        defval: 'line',
      },
    ],
  },
  {
    key: 'parkinsonVolatility',
    label: 'Parkinson Volatility',
    shortLabel: 'Parkinson Volatility',
    description: 'Parkinson high-low volatility over a configurable rolling window.',
    placement: 'overlay',
    color: CHART_COLORS.overlayVolatility,
    category: 'volatility',
    suite: 'volatility-spreads',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'volatility',
    family: CandleFamily.CANDLE_FAMILY_VOLATILITY,
    inputs: [
      {
        id: 'lookbackWindow',
        name: 'Lookback Window',
        type: 'integer',
        defval: 1,
      },
    ],
  },
  {
    key: 'totalLiqBase',
    label: 'Liquidations',
    shortLabel: 'Liquidations',
    description: 'Forced position closures during the candle in base or quote units.',
    placement: 'pane',
    color: '#F97316',
    category: 'liquidations',
    suite: 'liquidation-map',
    discoverable: true,
    recommendedVisual: 'histogram',
    unit: 'base',
    family: CandleFamily.CANDLE_FAMILY_LIQUIDATIONS,
    inputs: [
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'base',
      },
    ],
  },
  {
    key: 'buySellLiq',
    label: 'Liquidations (Buy/Sell)',
    shortLabel: 'Liquidations (Buy/Sell)',
    description:
      'Combined directional liquidation split with buy liquidations above zero and sell liquidations below zero in the same pane.',
    placement: 'pane',
    color: '#10B981',
    category: 'liquidations',
    suite: 'liquidation-map',
    discoverable: true,
    recommendedVisual: 'histogram',
    unit: 'base',
    family: CandleFamily.CANDLE_FAMILY_LIQUIDATIONS,
    inputs: [
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'base',
      },
    ],
  },
  {
    key: 'liqwap',
    label: 'LiqWAP',
    shortLabel: 'LiqWAP',
    description:
      'Average liquidation price level, useful as a liquidation-weighted support or resistance guide. Holds flat between liquidation events.',
    placement: 'overlay',
    color: CHART_COLORS.overlayLiquidationPrice,
    category: 'price',
    suite: 'price-overlays',
    discoverable: true,
    recommendedVisual: 'step-line',
    unit: 'price',
    family: CandleFamily.CANDLE_FAMILY_LIQUIDATIONS,
  },
  {
    key: 'totalTradeCount',
    label: 'Total Trade Count',
    shortLabel: 'Total Trade Count',
    description:
      'Total number of trades per candle, useful for spotting bursts of participation and activity.',
    placement: 'pane',
    color: '#A78BFA',
    category: 'microstructure',
    suite: 'market-microstructure',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'count',
    family: CandleFamily.CANDLE_FAMILY_ORDER_FLOW,
    inputs: [
      {
        id: 'visualStyle',
        name: 'Style',
        type: 'select',
        options: ['line', 'bars'],
        optionsTitles: { line: 'Line', bars: 'Bars' },
        defval: 'line',
      },
    ],
  },

  {
    key: 'netBuyTradeCount',
    label: 'Trade Count (Buy/Sell)',
    shortLabel: 'Trade Count (Buy/Sell)',
    description:
      'Net trade-count imbalance, useful for seeing whether buy or sell participation dominates.',
    placement: 'pane',
    color: '#F59E0B',
    category: 'microstructure',
    suite: 'market-microstructure',
    discoverable: true,
    recommendedVisual: 'zero-centered-histogram',
    unit: 'count',
    family: CandleFamily.CANDLE_FAMILY_ORDER_FLOW,
  },
  {
    key: 'avgTradeSize',
    label: 'Avg Trade Size',
    shortLabel: 'Avg Trade Size',
    description:
      'Average trade size per candle in base or quote units, useful for identifying larger participants and whale activity.',
    placement: 'pane',
    color: '#22D3EE',
    category: 'microstructure',
    suite: 'market-microstructure',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'base',
    family: CandleFamily.CANDLE_FAMILY_ORDER_FLOW,
    inputs: [
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'base',
      },
    ],
  },
  {
    key: 'vwapTwapSpread',
    label: 'VWAP/TWAP Spread',
    shortLabel: 'VWAP/TWAP Spread',
    description:
      'Difference between VWAP and TWAP, useful for spotting mean-reversion and execution dislocations.',
    placement: 'pane',
    color: '#38BDF8',
    category: 'volatility',
    suite: 'volatility-spreads',
    discoverable: true,
    recommendedVisual: 'zero-centered-histogram',
    unit: 'price',
    family: CandleFamily.CANDLE_FAMILY_VOLATILITY,
  },
  {
    key: 'netLiquidations',
    label: 'Net Liquidations',
    shortLabel: 'Net Liquidations',
    description:
      'Directional liquidation imbalance, with positive values indicating buy-side liquidations dominating.',
    placement: 'pane',
    color: '#F43F5E',
    category: 'liquidations',
    suite: 'liquidation-map',
    discoverable: true,
    recommendedVisual: 'zero-centered-histogram',
    unit: 'base',
    family: CandleFamily.CANDLE_FAMILY_LIQUIDATIONS,
    inputs: [
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'base',
      },
    ],
  },
  {
    key: 'volumeBaseBlockIncl',
    label: 'Volume',
    shortLabel: 'Volume',
    description: 'Total volume with configurable denomination and block-trade inclusion.',
    placement: 'overlay',
    color: CHART_COLORS.overlayBuyVolume,
    category: 'block',
    suite: 'block-included',
    // Disabled in favor of native TV Volume (overflow/z-order over the main
    // series wasn't holding up) — kept for its data plumbing, not offered as
    // a pickable indicator. Buy/Sell Volume stay discoverable below.
    discoverable: false,
    recommendedVisual: 'histogram',
    unit: 'base',
    family: CandleFamily.CANDLE_FAMILY_BLOCK_INCL,
    inputs: [
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'base',
      },
      {
        id: 'includeBlockTrades',
        name: 'Include Block Trades',
        type: 'bool',
        defval: true,
      },
    ],
  },
  {
    key: 'volumeQuoteBlockIncl',
    label: 'Quote Volume (incl. Block Trades)',
    shortLabel: 'Quote Volume (incl. Block Trades)',
    description: 'Total quote volume including block trades.',
    placement: 'pane',
    color: '#10B981',
    category: 'block',
    suite: 'block-included',
    discoverable: false,
    recommendedVisual: 'histogram',
    unit: 'quote',
    family: CandleFamily.CANDLE_FAMILY_BLOCK_INCL,
  },
  {
    key: 'buyVolumeBaseBlockIncl',
    label: 'Buy Volume',
    shortLabel: 'Buy Volume',
    description: 'Buy-side volume with configurable denomination and block-trade inclusion.',
    placement: 'overlay',
    color: CHART_COLORS.overlayBuyVolume,
    category: 'block',
    suite: 'block-included',
    discoverable: true,
    recommendedVisual: 'histogram',
    unit: 'base',
    family: CandleFamily.CANDLE_FAMILY_BLOCK_INCL,
    inputs: [
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'base',
      },
      {
        id: 'includeBlockTrades',
        name: 'Include Block Trades',
        type: 'bool',
        defval: true,
      },
    ],
  },
  {
    key: 'sellVolumeBaseBlockIncl',
    label: 'Sell Volume',
    shortLabel: 'Sell Volume',
    description: 'Sell-side volume with configurable denomination and block-trade inclusion.',
    placement: 'overlay',
    color: CHART_COLORS.overlaySellVolume,
    category: 'block',
    suite: 'block-included',
    discoverable: true,
    recommendedVisual: 'histogram',
    unit: 'base',
    family: CandleFamily.CANDLE_FAMILY_BLOCK_INCL,
    inputs: [
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'base',
      },
      {
        id: 'includeBlockTrades',
        name: 'Include Block Trades',
        type: 'bool',
        defval: true,
      },
    ],
  },
  {
    key: 'netBuyVolumeBaseBlockIncl',
    label: 'Net Buy Volume (incl. Block Trades)',
    shortLabel: 'Net Buy Volume (incl. Block Trades)',
    description: 'Net aggressive buying volume in base units including block trades.',
    placement: 'pane',
    color: '#F59E0B',
    category: 'block',
    suite: 'block-included',
    discoverable: false,
    recommendedVisual: 'zero-centered-histogram',
    unit: 'base',
    family: CandleFamily.CANDLE_FAMILY_BLOCK_INCL,
  },
  {
    key: 'netBuyVolumeQuoteBlockIncl',
    label: 'Net Buy Quote (incl. Block Trades)',
    shortLabel: 'Net Buy Quote (incl. Block Trades)',
    description: 'Net aggressive buying volume in quote units including block trades.',
    placement: 'pane',
    color: '#38BDF8',
    category: 'block',
    suite: 'block-included',
    discoverable: false,
    recommendedVisual: 'zero-centered-histogram',
    unit: 'quote',
    family: CandleFamily.CANDLE_FAMILY_BLOCK_INCL,
  },
  {
    key: 'openInterestBase',
    label: 'Open Interest',
    shortLabel: 'Open Interest',
    description: 'Outstanding perpetual/future open interest in base or quote units.',
    placement: 'pane',
    color: '#06B6D4',
    category: 'derivatives',
    suite: 'derivatives',
    discoverable: true,
    recommendedVisual: 'candles',
    unit: 'base',
    family: CandleFamily.CANDLE_FAMILY_FUNDING_OI,
    inputs: [
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'base',
      },
    ],
  },
  {
    key: 'openInterestQuote',
    label: 'Open Interest (Quote)',
    shortLabel: 'Open Interest (Quote)',
    description: 'Outstanding perpetual/future open interest in quote (USD notional) units.',
    placement: 'pane',
    color: '#0EA5E9',
    category: 'derivatives',
    suite: 'derivatives',
    discoverable: false,
    recommendedVisual: 'candles',
    unit: 'quote',
    family: CandleFamily.CANDLE_FAMILY_FUNDING_OI,
  },
  {
    key: 'aggregatedOpenInterest',
    label: 'Aggregated Open Interest',
    shortLabel: 'Aggregated Open Interest',
    description:
      'Aggregated open interest OHLC in quote notional, bucketed from derivative market data.',
    placement: 'pane',
    color: '#14B8A6',
    category: 'derivatives',
    suite: 'derivatives',
    discoverable: false,
    recommendedVisual: 'candles',
    unit: 'quote',
    family: CandleFamily.CANDLE_FAMILY_FUNDING_OI,
  },
  {
    key: 'fundingRate',
    label: 'Funding Rate',
    shortLabel: 'Funding Rate',
    description:
      'Per-interval perpetual funding rate (close of the bucket), positive when longs pay shorts.',
    placement: 'pane',
    color: '#A855F7',
    category: 'derivatives',
    suite: 'derivatives',
    discoverable: true,
    recommendedVisual: 'zero-centered-histogram',
    unit: 'percent',
    family: CandleFamily.CANDLE_FAMILY_FUNDING_OI,
  },
  {
    key: 'aggregatedFundingRateAvgClose',
    label: 'Aggregated Funding Rate AVG close',
    shortLabel: 'Funding Rate AVG',
    description: 'Configurable moving average of close funding-rate values.',
    placement: 'pane',
    color: '#22C55E',
    category: 'derivatives',
    suite: 'derivatives',
    discoverable: true,
    recommendedVisual: 'zero-centered-histogram',
    unit: 'percent',
    family: CandleFamily.CANDLE_FAMILY_FUNDING_OI,
    inputs: [
      {
        id: 'lookbackWindow',
        name: 'Lookback Window',
        type: 'integer',
        defval: 10,
      },
    ],
  },
  {
    key: 'fundingApr',
    label: 'Funding APR',
    shortLabel: 'Funding APR',
    description:
      'Annualized perpetual funding rate (close of the bucket), derived from the instrument funding interval.',
    placement: 'pane',
    color: '#8B5CF6',
    category: 'derivatives',
    suite: 'derivatives',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'percent',
    family: CandleFamily.CANDLE_FAMILY_FUNDING_OI,
  },
  {
    key: 'putCallRatioOi',
    label: 'Put/Call Ratio (OI)',
    shortLabel: 'PCR (OI)',
    description:
      'Options put/call open-interest ratio for the chart’s base currency (Amberdata, Deribit).',
    placement: 'pane',
    color: '#38BDF8',
    category: 'derivatives',
    suite: 'derivatives',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'base',
    source: 'putCallRatio',
  },
  {
    key: 'putCallRatioVolume',
    label: 'Put/Call Ratio (Volume)',
    shortLabel: 'PCR (Vol)',
    description:
      'Options put/call 24h-volume ratio for the chart’s base currency (Amberdata, Deribit).',
    placement: 'pane',
    color: '#F59E0B',
    category: 'derivatives',
    suite: 'derivatives',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'base',
    source: 'putCallRatio',
  },
  {
    key: 'optionsSkew25d',
    label: 'Options Skew',
    shortLabel: 'Options Skew',
    description:
      'Configurable-delta options skew (call IV − put IV) at the constant tenor nearest 30 days, for the chart’s base currency (Amberdata, Deribit).',
    placement: 'pane',
    color: '#C084FC',
    category: 'derivatives',
    suite: 'derivatives',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'volatility',
    source: 'skew',
    inputs: [
      {
        id: 'delta',
        name: 'Delta',
        type: 'select',
        options: ['5', '10', '15', '20', '25', '30', '35', '40', '45'],
        optionsTitles: {
          '5': '5Δ',
          '10': '10Δ',
          '15': '15Δ',
          '20': '20Δ',
          '25': '25Δ',
          '30': '30Δ',
          '35': '35Δ',
          '40': '40Δ',
          '45': '45Δ',
        },
        defval: '25',
      },
    ],
  },
  {
    key: 'optionsVolume',
    label: 'Options Volume',
    shortLabel: 'Options Vol',
    description:
      'Total options volume with configurable premium/notional and base/quote measures, bucketed at the chart timeframe.',
    placement: 'pane',
    color: '#22D3EE',
    category: 'derivatives',
    suite: 'derivatives',
    discoverable: true,
    recommendedVisual: 'histogram',
    unit: 'base',
    source: 'optionsVolume',
    inputs: [
      {
        id: 'valueType',
        name: 'Value',
        type: 'select',
        options: ['notional', 'premium'],
        optionsTitles: { notional: 'Notional', premium: 'Premium' },
        defval: 'notional',
      },
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'base',
      },
    ],
  },
  {
    key: 'obSpreadBps',
    label: 'Orderbook Spread (bps)',
    shortLabel: 'Spread bps',
    description: 'Time-weighted bid/ask spread in basis points, bucketed at the chart timeframe.',
    placement: 'pane',
    color: '#38BDF8',
    category: 'microstructure',
    suite: 'market-microstructure',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'base',
    source: 'orderbookMetrics',
  },
  {
    key: 'obPressureRatio',
    label: 'Orderbook Pressure Ratio',
    shortLabel: 'Pressure',
    description: 'Bid-versus-ask resting-liquidity pressure ratio per bucket.',
    placement: 'pane',
    color: '#A78BFA',
    category: 'microstructure',
    suite: 'market-microstructure',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'base',
    source: 'orderbookMetrics',
  },
  {
    key: 'obBidLiquidityTotal',
    label: 'Bid Liquidity (Total)',
    shortLabel: 'Bid Liquidity',
    description: 'Total resting bid-side liquidity per bucket.',
    placement: 'pane',
    color: '#00B68F',
    category: 'microstructure',
    suite: 'market-microstructure',
    discoverable: true,
    recommendedVisual: 'histogram',
    unit: 'quote',
    source: 'orderbookMetrics',
    inputs: [
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'quote',
      },
    ],
  },
  {
    key: 'obAskLiquidityTotal',
    label: 'Ask Liquidity (Total)',
    shortLabel: 'Ask Liquidity',
    description: 'Total resting ask-side liquidity per bucket.',
    placement: 'pane',
    color: '#DF484C',
    category: 'microstructure',
    suite: 'market-microstructure',
    discoverable: true,
    recommendedVisual: 'histogram',
    unit: 'quote',
    source: 'orderbookMetrics',
    inputs: [
      {
        id: 'measure',
        name: 'Measure',
        type: 'select',
        options: ['base', 'quote'],
        optionsTitles: { base: 'Base', quote: 'Quote' },
        defval: 'quote',
      },
    ],
  },
  {
    key: 'obDepthImbalance',
    label: 'Depth Imbalance',
    shortLabel: 'Depth Imbalance',
    description:
      'Bid/ask resting-liquidity imbalance within the selected depth tier: (bid − ask) / (bid + ask), from −1 (ask-heavy) to +1 (bid-heavy).',
    placement: 'pane',
    color: '#F59E0B',
    category: 'microstructure',
    suite: 'market-microstructure',
    discoverable: true,
    recommendedVisual: 'zero-centered-histogram',
    unit: 'base',
    source: 'orderbookMetrics',
    inputs: [
      {
        id: 'depthTierBps',
        name: 'Depth tier (bps)',
        type: 'select',
        options: ORDERBOOK_DEPTH_TIERS_BPS.map(String),
        optionsTitles: Object.fromEntries(
          ORDERBOOK_DEPTH_TIERS_BPS.map((bps) => [String(bps), `${bps} bps`])
        ),
        defval: String(DEFAULT_DEPTH_TIER_BPS),
      },
    ],
  },
  {
    key: 'crossExchangeOpenInterest',
    label: 'Aggregated Open Interest (All exchanges)',
    shortLabel: 'Agg OI (All exch.)',
    description:
      'Open interest of every perpetual and dated future on this asset, summed across venues. Each leg is carried forward between its own prints, so the total does not flicker with publish cadence. CME futures are included: they report open interest in contracts, which the API rescales by the contract size. History reflects the contracts listed today — a future that has since expired contributes nothing to the period it was live, so totals further back read low.',
    placement: 'pane',
    color: '#2DD4BF',
    category: 'cross-exchange',
    suite: 'cross-exchange',
    discoverable: true,
    recommendedVisual: 'area',
    unit: 'quote',
    source: 'crossExchangeOpenInterest',
    inputs: CROSS_EXCHANGE_OI_INPUTS,
  },
  {
    key: 'crossExchangeFundingRate',
    label: 'OI-Weighted Funding Rate (All exchanges)',
    shortLabel: 'OI-Wtd Funding',
    description:
      "Perpetual funding across venues, weighted by each leg's share of open interest in USD notional. APR is annualized per venue from its own funding interval before the weighting, so an hourly venue and an 8-hourly one are compared on one scale.",
    placement: 'pane',
    color: '#C084FC',
    category: 'cross-exchange',
    suite: 'cross-exchange',
    discoverable: true,
    recommendedVisual: 'zero-centered-histogram',
    unit: 'percent',
    source: 'crossExchangeFundingRate',
    inputs: CROSS_EXCHANGE_FUNDING_INPUTS,
  },
  {
    key: 'crossExchangeCvd',
    label: 'Aggregated CVD (All exchanges)',
    shortLabel: 'Agg CVD (All exch.)',
    description:
      'Cumulative volume delta over every spot, perpetual, future and ETF pair on this asset, summed across venues and rebased to zero at the left edge. Inverse contracts trade in USD notional; their base volume is recovered from the bucket VWAP before it joins the sum.',
    placement: 'pane',
    color: '#38BDF8',
    category: 'cross-exchange',
    suite: 'cross-exchange',
    discoverable: true,
    recommendedVisual: 'line',
    unit: 'base',
    source: 'crossExchangeCvd',
    inputs: CROSS_EXCHANGE_CVD_INPUTS,
  },
]

export const CHART_ANALYTICS_SUITES: ChartAnalyticsSuiteDefinition[] = [
  {
    key: 'price-overlays',
    label: 'Price Overlays',
    description: 'Main-chart overlays such as VWAP, TWAP, and liquidation-weighted price levels.',
    category: 'price',
    metrics: ['vwap', 'twap', 'liqwap'],
    settings: ['showNet'],
  },
  {
    key: 'advanced-volume',
    label: 'Advanced Volume Profile',
    description: 'Volume-focused analytics with unit switching and optional block-trade inclusion.',
    category: 'flow',
    metrics: ['netBuyVolumeBase', 'cumulativeVolumeDelta'],
    settings: ['unit', 'includeBlockTrades', 'buySellSplit'],
  },
  {
    key: 'order-flow-delta',
    label: 'Order Flow Delta',
    description:
      'Order-flow imbalance studies including cumulative delta and zero-centered delta percentage.',
    category: 'flow',
    metrics: ['deltaPercentage', 'netBuyVolumeBase', 'cumulativeVolumeDelta'],
    settings: ['unit', 'includeBlockTrades', 'showNet'],
  },
  {
    key: 'liquidation-map',
    label: 'Liquidation Map',
    description:
      'Liquidation pressure studies for total liquidations, directional liquidation imbalance, and liquidation intensity.',
    category: 'liquidations',
    metrics: ['totalLiqBase', 'buySellLiq', 'netLiquidations'],
    settings: ['unit', 'buySellSplit', 'showNet', 'showCounts'],
  },
  {
    key: 'market-microstructure',
    label: 'Market Microstructure',
    description:
      'Trade-count and average-trade-size studies for identifying retail versus institutional participation.',
    category: 'microstructure',
    metrics: ['totalTradeCount', 'netBuyTradeCount', 'avgTradeSize'],
    settings: ['buySellSplit', 'showNet', 'showCounts'],
  },
  {
    key: 'volatility-spreads',
    label: 'Volatility & Spreads',
    description:
      'Volatility and spread studies for mean-reversion, dispersion, and execution-quality context.',
    category: 'volatility',
    metrics: ['parkinsonVolatility', 'vwapTwapSpread'],
    settings: ['showNet'],
  },
  {
    key: 'block-included',
    label: 'Block-Included Metrics',
    description:
      'Volume and delta analytics that include block trades for users who want the fuller tape.',
    category: 'block',
    metrics: ['volumeBaseBlockIncl', 'buyVolumeBaseBlockIncl', 'sellVolumeBaseBlockIncl'],
    settings: ['unit', 'includeBlockTrades', 'buySellSplit', 'showNet'],
  },
  {
    key: 'derivatives',
    label: 'Derivatives',
    description:
      'Perpetual and future analytics for open interest and funding, useful for gauging positioning and carry.',
    category: 'derivatives',
    metrics: ['openInterestBase', 'fundingRate', 'aggregatedFundingRateAvgClose', 'fundingApr'],
    settings: ['unit'],
  },
  {
    key: 'cross-exchange',
    label: 'Cross-Exchange Aggregates',
    description:
      'Asset-level open interest, OI-weighted funding and CVD folded across every venue and contract, with per-venue and per-instrument-type toggles.',
    category: 'cross-exchange',
    metrics: ['crossExchangeOpenInterest', 'crossExchangeFundingRate', 'crossExchangeCvd'],
    settings: ['unit'],
  },
]

export const CHART_ANALYTICS_BY_KEY = Object.fromEntries(
  CHART_ANALYTICS_DEFINITIONS.map((definition) => [definition.key, definition])
) as Record<ChartAnalyticsMetric, ChartAnalyticsDefinition>

const LEGACY_CHART_ANALYTICS_STUDY_NAMES: Partial<Record<string, ChartAnalyticsMetric>> = {
  'OrderX Base Volume (incl. Block Trades)': 'volumeBaseBlockIncl',
  'OrderX Buy Volume (incl. Block Trades)': 'buyVolumeBaseBlockIncl',
  'OrderX Sell Volume (incl. Block Trades)': 'sellVolumeBaseBlockIncl',
  'OrderX Open Interest (Base)': 'openInterestBase',
  'OrderX Options Skew (25Δ)': 'optionsSkew25d',
}

/** Resolve both current and pre-merge study names so restored layouts keep
 * participating in family masks, toolbar state, and own-fetch subscriptions. */
export function getChartAnalyticsMetricByStudyName(
  studyName: string
): ChartAnalyticsMetric | undefined {
  return (
    LEGACY_CHART_ANALYTICS_STUDY_NAMES[studyName] ??
    CHART_ANALYTICS_DEFINITIONS.find((definition) => `OrderX ${definition.label}` === studyName)
      ?.key
  )
}

/** Hidden monotonic input bumped via IStudyApi.setInputValues to force TV to
 *  recompute a study from scratch after runtime-only data or its visible-range
 *  baseline changes. Injected by getEffectiveStudyInputs — never declared on
 *  definitions, never rendered (isHidden), excluded from series cache keys. */
export const REFRESH_NONCE_INPUT: StudyInputSpec = {
  id: REFRESH_NONCE_INPUT_ID,
  name: 'Refresh nonce',
  type: 'integer',
  defval: 0,
  isHidden: true,
}

/** The metainfo/runtime input list for a definition: declared inputs, plus the
 *  hidden refresh nonce appended LAST for own-fetch studies and visible-range
 *  CVD rebasing. Append-last keeps declared input indexes stable. */
export function getEffectiveStudyInputs(definition: ChartAnalyticsDefinition): StudyInputSpec[] {
  const declared = definition.inputs ?? []
  const needsRuntimeRefresh =
    definition.source !== undefined || definition.key === 'cumulativeVolumeDelta'
  return needsRuntimeRefresh ? [...declared, REFRESH_NONCE_INPUT] : declared
}

export const CHART_ANALYTICS_SUITES_BY_KEY = Object.fromEntries(
  CHART_ANALYTICS_SUITES.map((suite) => [suite.key, suite])
) as Record<ChartAnalyticsSuiteKey, ChartAnalyticsSuiteDefinition>

export const OI_OHLC_FIELDS: Partial<
  Record<
    ChartAnalyticsMetric,
    {
      open: keyof CandleList
      high: keyof CandleList
      low: keyof CandleList
      close: keyof CandleList
    }
  >
> = {
  openInterestBase: {
    open: 'openInterestBaseOpen',
    high: 'openInterestBaseHigh',
    low: 'openInterestBaseLow',
    close: 'openInterestBaseClose',
  },
  openInterestQuote: {
    open: 'openInterestQuoteOpen',
    high: 'openInterestQuoteHigh',
    low: 'openInterestQuoteLow',
    close: 'openInterestQuoteClose',
  },
  aggregatedOpenInterest: {
    open: 'openInterestQuoteOpen',
    high: 'openInterestQuoteHigh',
    low: 'openInterestQuoteLow',
    close: 'openInterestQuoteClose',
  },
}

export const CHART_ANALYTICS_CATEGORY_LABELS: Record<ChartAnalyticsCategory, string> = {
  price: 'Main chart overlays',
  flow: 'Volume & delta',
  liquidations: 'Liquidations',
  microstructure: 'Trade counts & sizing',
  volatility: 'Volatility & spreads',
  block: 'Block-trade included',
  derivatives: 'Open interest & funding',
  'cross-exchange': 'All exchanges',
}

export const CHART_ANALYTICS_PRESETS: Array<{
  key:
    | 'price-context'
    | 'order-flow'
    | 'liquidations'
    | 'microstructure'
    | 'volatility'
    | 'block-incl'
    | 'derivatives'
    | 'cross-exchange'
    | 'risk'
  label: string
  description: string
  metrics: ChartAnalyticsMetric[]
}> = [
  {
    key: 'price-context',
    label: 'Price Context',
    description: 'Show benchmark price overlays that help anchor trend and execution.',
    metrics: ['vwap', 'twap', 'liqwap'],
  },
  {
    key: 'order-flow',
    label: 'Order Flow',
    description: 'Show buyer versus seller pressure and net aggressive flow.',
    metrics: ['deltaPercentage', 'netBuyVolumeBase', 'cumulativeVolumeDelta'],
  },
  {
    key: 'liquidations',
    label: 'Liquidations',
    description: 'Show total, directional, and net liquidation pressure.',
    metrics: ['totalLiqBase', 'buySellLiq', 'netLiquidations', 'liqwap'],
  },
  {
    key: 'microstructure',
    label: 'Microstructure',
    description:
      'Show participation and sizing signals from total and net trade counts plus average trade size.',
    metrics: ['totalTradeCount', 'netBuyTradeCount', 'avgTradeSize'],
  },
  {
    key: 'volatility',
    label: 'Volatility',
    description: 'Show Parkinson volatility and VWAP/TWAP spread context.',
    metrics: ['parkinsonVolatility', 'vwapTwapSpread'],
  },
  {
    key: 'block-incl',
    label: 'Block Incl',
    description: 'Show the block-trade-included volume and delta metrics.',
    metrics: ['volumeBaseBlockIncl', 'buyVolumeBaseBlockIncl', 'sellVolumeBaseBlockIncl'],
  },
  {
    key: 'derivatives',
    label: 'Derivatives',
    description: 'Show open interest and funding to gauge positioning and carry.',
    metrics: ['openInterestBase', 'fundingRate', 'aggregatedFundingRateAvgClose', 'fundingApr'],
  },
  {
    key: 'cross-exchange',
    label: 'All Exchanges',
    description:
      'Show the asset-level aggregates: open interest, OI-weighted funding and CVD across every venue.',
    metrics: ['crossExchangeOpenInterest', 'crossExchangeFundingRate', 'crossExchangeCvd'],
  },
  {
    key: 'risk',
    label: 'Risk',
    description: 'Show liquidation activity, volatility, and participation stress together.',
    metrics: [
      'totalLiqBase',
      'buySellLiq',
      'netLiquidations',
      'parkinsonVolatility',
      'vwapTwapSpread',
      'totalTradeCount',
      'netBuyTradeCount',
      'avgTradeSize',
    ],
  },
]

export function getEnabledChartAnalytics(analytics?: ChartAnalyticsOption): ChartAnalyticsMetric[] {
  if (!analytics) return []

  return CHART_ANALYTICS_DEFINITIONS.filter((definition) => analytics[definition.key]?.enabled).map(
    (definition) => definition.key
  )
}
